import { API_URL } from '@/constants/api';
import { readJson } from '@/shared/utils/json';
import type { ActivityLog, ActivityLogResponse } from '../types';

type ActivityLogsApiResponse = {
  data: Array<ActivityLog & { details: ActivityLog['details'] | string }>;
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
};

/**
 * Normalizes activity log details field.
 * Backend may return details as either:
 * - JSON string: "{\"stage\":\"At trial funnel\"}" (for new events like "MTO Blocked")
 * - Object: { message: "..." } (for existing events like "Liobo", "SKU Swapped")
 *
 * This function ensures details is always an object to match TypeScript type.
 */
const normalizeActivityLogDetails = (
  log: ActivityLog & { details: ActivityLog['details'] | string },
): ActivityLog => {
  if (typeof log.details === 'string') {
    try {
      return { ...log, details: JSON.parse(log.details) as ActivityLog['details'] };
    } catch (e) {
      // If parsing fails, set to null to match TypeScript type
      // This indicates invalid JSON format from backend
      console.warn('Failed to parse activity log details, setting to null:', e, log.details);
      return { ...log, details: null };
    }
  }
  // If details is already an object or null, return as-is
  return log;
};

export const fetchActivityLogs = async (
  shopifyCustomerId: string,
  page = 1,
  perPage = 20,
  category?: string,
  level?: string,
): Promise<ActivityLogResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    perPage: perPage.toString(),
  });

  if (category) {
    params.append('category', category);
  }

  if (level) {
    params.append('level', level);
  }

  const response = await fetch(
    `${API_URL}/admin/customers/${shopifyCustomerId}/activity-logs?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch activity logs');
  }

  const json = await readJson<ActivityLogsApiResponse>(response);
  // Normalize details field to handle both string and object formats
  const items = json.data.map(normalizeActivityLogDetails);

  return {
    items,
    pagination: {
      page: json.meta.current_page,
      perPage: json.meta.per_page,
      total: json.meta.total,
      totalPages: json.meta.last_page,
    },
  };
};
